export type RideType = "now" | "schedule";

export type NowServiceTier = "eco" | "biz" | "lux";

export type RouteCoords = { lat: number; lng: number };

export type RouteWaypoint = {
  id: string;
  address: string;
  coords: RouteCoords | null;
};

export type MapMarkerId = "pickup" | "dropoff" | `waypoint-${string}`;

export type MapMarker = {
  id: MapMarkerId;
  lat: number;
  lng: number;
  label: string;
  waypointIndex?: number;
};

export type OrderFormMapState = {
  markers: MapMarker[];
  onMarkerDrag?: (markerId: MapMarkerId, coords: RouteCoords) => void;
};

export function isScheduleRide(rideType: RideType): boolean {
  return rideType === "schedule";
}

export type FavoriteAddress = {
  id: number;
  user_id: number;
  address_name: string;
  lat: number;
  lng: number;
};

export type OrderFormState = {
  vehicleId: string;
  carClass: number | null;
  pickup: string;
  dropoff: string;
  pickupCoords: RouteCoords | null;
  dropoffCoords: RouteCoords | null;
  waypoints: RouteWaypoint[];
  name: string;
  phoneDialCode: string;
  phone: string;
  userId: number | null;
  date: string;
  time: string;
  passengers: number;
  luggage: number;
  transferMode: boolean;
  durationHours: number;
  comment: string;
  flightNumber: string;
  childSeat: boolean;
};

export type OrderFormLocale = {
  headerTitle: string;
  route: {
    from: string;
    fromPlaceholder: string;
    to: string;
    toPlaceholder: string;
    swapAria: string;
    clearAddressAria: string;
    loading: string;
    noResults: string;
    addOnMap: string;
    waypointLabel: string;
    waypointPlaceholder: string;
    addWaypoint: string;
    removeWaypointAria: string;
    favoriteAddressesSection: string;
    searchResultsSection: string;
  };
  contact: {
    title: string;
    namePlaceholder: string;
    phonePlaceholder: string;
    dialCodeAria: string;
    invalidPhone: string;
  };
  rideType: {
    label: string;
    orderNow: string;
    schedule: string;
    orderNowDescription: string;
    scheduleDescription: string;
  };
  schedule: {
    dateTimeLabel: string;
    passengersLabel: string;
    luggageLabel: string;
    flightNumberLabel: string;
    flightNumberPlaceholder: string;
    transferModeLabel: string;
    transferModeTransfer: string;
    transferModeRental: string;
    transferModeTransferDescription: string;
    transferModeRentalDescription: string;
    airportAddress: string;
    durationLabel: string;
    durationMinNote: string;
    durationFullDay: string;
    timezoneNote: string;
  };
  childSeatLabel: string;
  elit?: {
    vehicleLabel: string;
    vehiclePanelTitle: string;
    vehiclePanelSubtitle: string;
    chooseCarLabel: string;
    luggageCountLabel: string;
    passengersCountLabel: string;
    transferLabel: string;
    hourlyRateLabel: string;
    dailyRateLabel: string;
    pricePerKmLabel: string;
    moreCarDetailsLabel: string;
  };
  comment: { label: string; placeholder: string };
  price: {
    label: string;
    loading: string;
    unavailable: string;
    error: string;
  };
  passenger: {
    label: string;
    placeholder: string;
    loading: string;
    noResults: string;
  };
  submitting: string;
  minHours: string;
};

export interface GeocodeSuggestion {
  formattedAddress: string;
  lat: number;
  lng: number;
}

export const normalizeGeocodeResults = (data: unknown): GeocodeSuggestion[] => {
  const arr = Array.isArray(data) ? data : (data as any)?.results ?? (data as any)?.data ?? [];
  return (arr as any[])
    .map((item: any): GeocodeSuggestion | null => {
      const formattedAddress: string =
        item.formattedAddress ?? item.display_name ?? item.name ?? item.label ?? "";
      const lat = parseFloat(item.lat ?? item.latitude ?? "");
      const lng = parseFloat(item.lng ?? item.lon ?? item.longitude ?? "");
      return formattedAddress && !isNaN(lat) && !isNaN(lng)
        ? { formattedAddress, lat, lng }
        : null;
    })
    .filter(Boolean) as GeocodeSuggestion[];
};

export const normalizeReverseAddress = (res: unknown): string => {
  const r = res as any;
  return r?.display_name ?? r?.formattedAddress ?? r?.name ?? "Custom location";
};
